import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for managing resend cooldown timer
 * @param {number} cooldownSeconds - Duration of cooldown in seconds
 * @param {string|null} storageKey - Optional sessionStorage key for persistence across refreshes
 * @returns {Object} - { canResend, timeLeft, startCooldown }
 */
export const useResendTimer = (cooldownSeconds = 120, storageKey = null) => {
  const [canResend, setCanResend] = useState(true);
  const [timeLeft, setTimeLeft] = useState(0);
  const timerRef = useRef(null);

  const beginCountdown = useCallback((seconds) => {
    setCanResend(false);
    setTimeLeft(seconds);

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          clearInterval(timerRef.current);
          timerRef.current = null;
          if (storageKey) {
            try {
              sessionStorage.removeItem(storageKey);
            } catch {}
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [storageKey]);

  // Resume cooldown from sessionStorage on mount
  useEffect(() => {
    if (!storageKey) return;

    try {
      const stored = sessionStorage.getItem(storageKey);
      if (stored) {
        const endTime = parseInt(stored, 10);
        const remaining = Math.ceil((endTime - Date.now()) / 1000);
        if (remaining > 0) {
          beginCountdown(remaining);
        } else {
          sessionStorage.removeItem(storageKey);
        }
      }
    } catch {}
  }, [storageKey, beginCountdown]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const startCooldown = () => {
    if (storageKey) {
      try {
        const endTime = Date.now() + cooldownSeconds * 1000;
        sessionStorage.setItem(storageKey, endTime.toString());
      } catch {}
    }
    beginCountdown(cooldownSeconds);
  };

  return { canResend, timeLeft, startCooldown };
};
